import { Layout, Text } from '@oneaudi/unified-web-components';
import { useCallback, useMemo } from 'react';
import MonthlyRateFootnote from '~/app/compositions/content/calculation-info/components/monthly-rate-footnote';
import PriceFootnote from '~/app/compositions/content/calculation-info/components/price-footnote';
import { PriceSectionStyle } from '~/app/compositions/content/components/styles/section-style';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { useIframeDialog } from '~/components/dialog/iframe-dialog/iframe-dialog';
import { StyledTextLinkButton } from '~/components/dialog/styles/styled-dialog';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { useCarlineQuery } from '~/graphql/hooks/use-configured-car-queries';

export function PriceSection({
	totalPrice,
	monthlyRate,
	remark,
	showSpecialOffers,
	disclaimer,
}: {
	totalPrice: string;
	monthlyRate: string | null | undefined;
	remark: string | null | undefined;
	showSpecialOffers: boolean;
	disclaimer?: string;
}) {
	const { i18n } = useI18n();
	const { carIdentifier } = useConfigurator();
	const { config } = useMarketConfig();
	const { showIframeDialog } = useIframeDialog();
	const carlineData = useCarlineQuery(carIdentifier, config.financingInput);

	const carType =
		carlineData?.carline?.configuredCar?.carlineStructureCarline?.vehicleType ||
		carlineData?.carline?.configuredCar?.technicalDataV2?.modelAttributes?.driveTrain;
	const priceDisclaimerText = useMemo(() => {
		if (config.enablePriceDisclaimer) {
			return carType
				? carType === 'BEV'
					? i18n('ngc.remarks.price.legalBEV')
					: i18n('ngc.remarks.price.legalICE')
				: undefined;
		}
		return null;
	}, [config.enablePriceDisclaimer, carType, i18n]);

	const onButtonClick = useCallback(
		(url: string, eventName: string, label: string) => {
			config.useIframe ? showIframeDialog({ url, name: eventName.split(' ').join('-') }) : window.open(url);
			appTrackingService.summaryClickEvent({
				eventAction: EventAction.InternalLink,
				eventName: `configurator (configuration overview) - click to open ${eventName}`,
				componentName: 'configurator-configuration-overview',
				label,
				targetURL: url,
			});
			config.useIframe &&
				appTrackingService.viewChangeEvent({
					eventName: `configurator - ${eventName}`,
					componentName: eventName.split(' ').join('-'),
				});
		},
		[config.useIframe, showIframeDialog],
	);

	return (
		<PriceSectionStyle>
			{/* eslint-disable-next-line */}
			<div tabIndex={0} className="price-grid">
				<Text tag={'p'} variant={{ type: 'copy', order: '1', style: 'normal' }}>
					{i18n('ngc.priceDetails.totalPrice')}
					{config.enablePriceDisclaimer && <PriceFootnote text={priceDisclaimerText} />}
				</Text>

				<Text className="alignRight" tag={'p'} variant={{ type: 'copy', order: '1', style: 'normal' }}>
					{totalPrice}
				</Text>
			</div>
			{monthlyRate && !config.hideMonthlyRates && (
				/* eslint-disable-next-line */
				<div tabIndex={0} className="price-grid">
					<Text tag={'p'} variant={{ type: 'copy', order: '2', style: 'normal' }}>
						{i18n('ngc.priceDetails.privateLeasing')}
					</Text>

					<Text tag={'p'} variant={{ type: 'copy', order: '2', style: 'normal' }} className="alignRight">
						{monthlyRate}
						<MonthlyRateFootnote text={disclaimer} />
					</Text>
				</div>
			)}
			<Layout
				direction={{
					default: 'column',
					md: 'row',
				}}
				columnGap="24px"
				rowGap="8px">
				{config.deliveryCostUrl && (
					<StyledTextLinkButton
						size="sm"
						variant="standalone"
						onClick={() =>
							onButtonClick(
								config.deliveryCostUrl,
								'delivery cost layer',
								i18n('ngc.priceDetails.deliveryCosts'),
							)
						}>
						{i18n('ngc.priceDetails.deliveryCosts')}
					</StyledTextLinkButton>
				)}
				{showSpecialOffers && (
					<StyledTextLinkButton
						size="sm"
						variant="standalone"
						onClick={() =>
							onButtonClick(
								config.specialOfferUrl,
								'special offers layer',
								i18n('ngc.actions.viewSpecialOffers'),
							)
						}>
						{i18n('ngc.actions.viewSpecialOffers')}
					</StyledTextLinkButton>
				)}
			</Layout>

			{remark && (
				/* eslint-disable-next-line  */
				<div tabIndex={0}>
					<Text className="mt-4" tag={'p'} variant={{ type: 'copy', order: '2', style: 'normal' }}>
						<span dangerouslySetInnerHTML={{ __html: remark }} />
					</Text>
				</div>
			)}
		</PriceSectionStyle>
	);
}
